import {useEffect, useState, type CSSProperties} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {Check} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {
  PropertyLinkPhoto,
  measurePropertyHeaderFrame,
  useArrivalProperty,
} from 'components/arrival';
import {useSummary} from 'context/summary';
import {useGuestGroup} from 'hooks/useGuestGroup';
import {usePendingPayments} from './usePendingPayments';
import type {RegisteredGuest} from './steps/LeftStep';
import styles from './instantCheckIn.module.css';

const LIST_START_S = 1.35;
const LIST_STEP_S = 0.32;
const LOADING_S = 3.75;

type Row = {key: string; label: string; done: boolean};

const PAYMENT_ACTIONS = {
  booking_payments: 'instant_check_in.pay_reservation',
  property_protection: 'instant_check_in.leave_deposit',
  taxes: 'instant_check_in.pay_tourist_tax',
} as const;

type Props = {
  registered: RegisteredGuest[];
  onFinished: () => void;
};

type Plan = {rows: Row[]; todoCount: number; completeCount: number};

function FinalScreen({registered, onFinished}: Props) {
  const {t} = useTranslation();
  const property = useArrivalProperty();
  const {summaryStatus} = useSummary();
  const {totalGuests, totalRegisteredGuests, isLoading, isFetching} = useGuestGroup();
  const payments = usePendingPayments();
  const [frame] = useState(measurePropertyHeaderFrame);
  const [plan, setPlan] = useState<Plan | null>(null);

  const isDataSettled =
    !isLoading &&
    !isFetching &&
    (summaryStatus === 'success' || summaryStatus === 'error');

  useEffect(
    function freezePlanOnceSettled() {
      if (plan || !isDataSettled) return;

      const complete = registered.filter(guest => guest.isComplete);
      const guestsLeft = Math.max(0, totalGuests - totalRegisteredGuests);
      const todo: Row[] = [
        ...(guestsLeft > 0
          ? [
              {
                key: 'guests',
                label:
                  totalRegisteredGuests > 0
                    ? t('instant_check_in.register_more_guests', {count: guestsLeft})
                    : t('instant_check_in.register_guests', {count: guestsLeft}),
                done: false,
              },
            ]
          : []),
        ...payments.map(payment => ({
          key: payment.kind,
          label: t(PAYMENT_ACTIONS[payment.kind]),
          done: false,
        })),
      ];

      setPlan({
        rows: [
          ...complete.map(guest => ({
            key: guest.id,
            label: t('instant_check_in.name_registered', {
              name: guest.name.split(' ')[0],
            }),
            done: true,
          })),
          ...todo,
        ],
        todoCount: todo.length,
        completeCount: complete.length,
      });
    },
    [plan, isDataSettled, registered, totalGuests, totalRegisteredGuests, payments, t],
  );

  const rows = plan?.rows ?? [];
  const isAllSet = plan?.todoCount === 0;
  const listEnds = LIST_START_S + rows.length * LIST_STEP_S;

  useEffect(
    function openBookingWhenLoaded() {
      if (!plan) return;
      const timeout = window.setTimeout(onFinished, (listEnds + LOADING_S) * 1000);
      return () => window.clearTimeout(timeout);
    },
    [plan, listEnds, onFinished],
  );

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="instant-check-in-final-title"
      className={styles.final}
      style={frame}
    >
      <PropertyLinkPhoto />
      <div
        aria-live="polite"
        className="relative z-[1] flex w-full max-w-[420px] flex-col items-center px-6 text-center text-white"
      >
        {!plan ? null : isAllSet ? (
          <>
            <span
              className={cn(
                styles.reveal,
                'mb-4 grid size-16 place-items-center rounded-full bg-[var(--chekin-success)]',
              )}
              style={{'--d': '0.6s'} as CSSProperties}
            >
              <Check size={34} strokeWidth={3} />
            </span>
            <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-white/75">
              {t('instant_check_in.check_in_done')}
            </p>
            <h2
              id="instant-check-in-final-title"
              className="mt-1.5 text-[28px] font-bold leading-[1.15] tracking-[-0.02em]"
            >
              {t('instant_check_in.all_set_title')}
            </h2>
            {property.name && (
              <p className="mt-2 text-[15px] text-white/85">
                {t('instant_check_in.everyone_checked_in', {property: property.name})}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-white/75">
              {plan.completeCount
                ? t('instant_check_in.almost_there')
                : t('instant_check_in.next_steps')}
            </p>
            <h2
              id="instant-check-in-final-title"
              className="mt-1.5 text-[28px] font-bold leading-[1.15] tracking-[-0.02em]"
            >
              {t('instant_check_in.steps_left', {count: plan.todoCount})}
            </h2>
            <ul className="mt-5 flex w-full max-w-[320px] flex-col text-left">
              {rows.map((row, index) => (
                <li
                  key={row.key}
                  className={cn(
                    styles.reveal,
                    'flex items-center gap-3 py-[9px] text-[15px] font-semibold [&+li]:border-t [&+li]:border-white/[0.12]',
                    row.done && 'text-white/90',
                  )}
                  style={
                    {'--d': `${LIST_START_S + index * LIST_STEP_S}s`} as CSSProperties
                  }
                >
                  <span
                    className={cn(
                      'grid size-6 shrink-0 place-items-center rounded-full',
                      row.done
                        ? 'bg-[color-mix(in_srgb,var(--chekin-success)_70%,var(--chekin-color-white))] text-[var(--chekin-color-brand-navy)]'
                        : cn(
                            styles.pendingDot,
                            'bg-[color-mix(in_srgb,var(--chekin-warning)_25%,transparent)]',
                          ),
                    )}
                    aria-hidden
                  >
                    {row.done && <Check size={14} strokeWidth={3} />}
                  </span>
                  {row.label}
                </li>
              ))}
            </ul>
          </>
        )}

        {plan && (
          <p
            className={cn(
              styles.reveal,
              styles.opening,
              'mt-5 flex items-center justify-center gap-2 text-[14px] font-semibold text-white/80',
            )}
            style={{'--d': `${isAllSet ? 1.2 : listEnds}s`} as CSSProperties}
          >
            {t('instant_check_in.opening_booking')}
            <i aria-hidden />
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}

export {FinalScreen};
